import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { ChevronDownIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { useSeasons, downloadMonthReport, downloadCurrentSeasonReport, useResetSchedule } from '@/services/gamification';
import GradientCard from './GradientCard';
import { parseApiDate } from '../../utils/apiDates';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Year + month from a stored (UTC) `started_at` ISO string, parsed from the
 *  string directly so it matches the backend's stored month key (no tz drift). */
const parseYearMonth = (iso: string): { year: number; month: number } => ({
  year: Number(iso.slice(0, 4)),
  month: Number(iso.slice(5, 7)),
});

const selectClasses =
  'flex-1 appearance-none bg-transparent text-[15px] leading-6 text-text-primary ' +
  'outline-none cursor-pointer pr-6 border-none disabled:cursor-not-allowed';

/**
 * "Previous Months — Reporting": lets a trainer pick a past month (only months
 * that have a completed season are selectable — the ongoing month is excluded)
 * and download a CSV of that season's learners, points and claimed prizes.
 */
const PreviousMonthsReportCard: React.FC = () => {
  const { data: seasons, isLoading } = useSeasons();
  const { data: schedule } = useResetSchedule();

  const [year, setYear] = useState<number | null>(null);
  const [month, setMonth] = useState<number | null>(null);
  const [downloading, setDownloading] = useState(false);

  // Closed seasons grouped by year, PLUS the month in progress — selecting
  // it downloads the live report instead of an archived one.
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const monthsByYear = useMemo(() => {
    const map = new Map<number, number[]>();
    (seasons ?? [])
      .filter((s) => !s.is_current && s.ended_at)
      .forEach((s) => {
        const { year: y, month: m } = parseYearMonth(s.started_at);
        const months = map.get(y) ?? [];
        if (!months.includes(m)) months.push(m);
        map.set(y, months);
      });
    const cur = map.get(currentYear) ?? [];
    if (!cur.includes(currentMonth)) cur.push(currentMonth);
    map.set(currentYear, cur);
    for (const months of map.values()) months.sort((a, b) => b - a);
    return map;
  }, [seasons, currentYear, currentMonth]);

  const years = useMemo(
    () => [...monthsByYear.keys()].sort((a, b) => b - a),
    [monthsByYear]
  );
  const months = year ? monthsByYear.get(year) ?? [] : [];
  const hasReports = years.length > 0;
  const isCurrentSelection = year === currentYear && month === currentMonth;

  // Default to the most recent reportable month once data loads.
  useEffect(() => {
    if (!hasReports) return;
    if (year === null || !monthsByYear.has(year)) {
      const firstYear = years[0];
      setYear(firstYear);
      setMonth((monthsByYear.get(firstYear) ?? [])[0] ?? null);
    }
  }, [hasReports, years, monthsByYear, year]);

  const handleYearChange = (nextYear: number) => {
    setYear(nextYear);
    setMonth((monthsByYear.get(nextYear) ?? [])[0] ?? null);
  };

  const triggerCsvDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDownload = async () => {
    if (year === null || month === null) return;
    const suffix = isCurrentSelection ? '-in-progress' : '';
    const filename = `gamification-report-${year}-${String(month).padStart(2, '0')}${suffix}.csv`;
    setDownloading(true);
    try {
      const blob = isCurrentSelection
        ? await downloadCurrentSeasonReport()
        : await downloadMonthReport(year, month);
      triggerCsvDownload(blob, filename);
      toast.success('Report downloaded');
    } catch {
      toast.error('Could not download the report. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <GradientCard className="rounded-3xl p-6">
      <h2 className="text-[1.4rem] font-medium leading-snug text-primary-500 dark:text-primary-300">
        Previous Months — Reporting
      </h2>
      <p className="mt-2 max-w-2xl body-small text-text-secondary">
        Pick a month and download its CSV report. The month in progress is
        marked and exports live standings — every prize claimed so far, and when.
        {schedule?.next_reset_at && (
          <>
            {' '}
            The current period appears here after its reset on{' '}
            {parseApiDate(schedule.next_reset_at).toLocaleDateString(undefined, {
              month: 'long',
              day: 'numeric',
            })}
            .
          </>
        )}
      </p>

      {isLoading ? (
        <p className="mt-8 body-small text-text-secondary">Loading available months…</p>
      ) : !hasReports ? (
        <p className="mt-8 body-small text-text-secondary">
          No completed months yet — a monthly report appears here after the first reset.
        </p>
      ) : (
        <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-end">
          {/* Year */}
          <label className="flex flex-col gap-1.5 sm:w-40">
            <span className="text-caption font-semibold text-text-secondary">Year</span>
            <div className="flex h-12 items-center rounded-xl border border-grey-500/20 px-3.5">
              <select
                value={year ?? ''}
                onChange={(e) => handleYearChange(Number(e.target.value))}
                className={selectClasses}
                aria-label="Report year"
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="w-[18px] h-[18px] text-text-secondary pointer-events-none shrink-0" />
            </div>
          </label>

          {/* Month */}
          <label className="flex flex-col gap-1.5 sm:w-52">
            <span className="text-caption font-semibold text-text-secondary">Month</span>
            <div className="flex h-12 items-center rounded-xl border border-grey-500/20 px-3.5">
              <select
                value={month ?? ''}
                onChange={(e) => setMonth(Number(e.target.value))}
                className={selectClasses}
                aria-label="Report month"
              >
                {months.map((m) => (
                  <option key={m} value={m}>
                    {MONTH_NAMES[m - 1]}
                    {year === currentYear && m === currentMonth ? ' — in progress' : ''}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="w-[18px] h-[18px] text-text-secondary pointer-events-none shrink-0" />
            </div>
          </label>

          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading || year === null || month === null}
            className="btn-primary h-12 font-bold inline-flex items-center justify-center gap-2 active:scale-[0.97] disabled:opacity-60 disabled:active:scale-100"
          >
            <ArrowDownTrayIcon className="w-5 h-5" />
            {downloading ? 'Preparing…' : 'Download CSV'}
          </button>
        </div>
      )}

    </GradientCard>
  );
};

export default PreviousMonthsReportCard;
